/**
 * Reading attribute-edit provenance: which override is actually in effect, and
 * which are history. Kept out of the fetch so the rules are stated once and
 * testable — every surface that shows an "edited" marker must apply them.
 */

import type { ProductAttributeEdit } from '../types/catalog-operations.types';

/**
 * Whether an edit is still in effect. An override does **not** survive
 * re-enrichment: a re-run writes a fresh enrichment row carrying the agent's
 * value, while the provenance row persists. So an edit recorded against a
 * different run is history, and marking it "edited" would claim an override that
 * no longer applies.
 */
export function isLiveAttributeEdit(
  edit: ProductAttributeEdit,
  currentWorkflowId: string | undefined,
): boolean {
  // With no current run to compare against, nothing can be proven live.
  // Withholding the marker is the safe direction — the failure mode of guessing
  // is asserting an override that isn't there.
  if (currentWorkflowId === undefined || currentWorkflowId === '') return false;
  return edit.workflowId === currentWorkflowId;
}

/** Per-field provenance: the override in effect, plus everything recorded before it. */
export interface FieldEditProvenance {
  /** Newest edit for the field, present only when it survived the current run. */
  live?: ProductAttributeEdit;
  /** Every edit for the field, newest-first — includes `live` and stale rows. */
  history: ProductAttributeEdit[];
}

/**
 * Groups the flat, newest-first edit list by field.
 *
 * The endpoint's ordering is the whole mechanism: the FIRST occurrence of a field
 * is that attribute's latest edit, so nothing needs sorting. Only that first row
 * is a candidate for the live marker, and it earns it solely by matching the
 * current run.
 */
export function indexAttributeEdits(
  edits: ProductAttributeEdit[],
  currentWorkflowId: string | undefined,
): Map<string, FieldEditProvenance> {
  const byField = new Map<string, FieldEditProvenance>();

  for (const edit of edits) {
    const existing = byField.get(edit.field);
    if (existing) {
      existing.history.push(edit);
      continue;
    }
    byField.set(edit.field, {
      history: [edit],
      ...(isLiveAttributeEdit(edit, currentWorkflowId) ? { live: edit } : {}),
    });
  }

  return byField;
}

/**
 * Flattens a raw edit value for display. Values arrive in the attribute's own
 * JSON shape, so this is the one place that reads them: lists render the way the
 * UI shows them elsewhere (`|`-joined), objects as `key: value` pairs, and a
 * cleared attribute as an explicit word rather than a blank the reader has to
 * interpret.
 */
export function formatEditValue(value: unknown): string {
  if (value === null || value === undefined) return 'Empty';
  if (typeof value === 'string') return value === '' ? 'Empty' : value;
  if (Array.isArray(value)) {
    const items: unknown[] = value;
    return items.length === 0 ? 'Empty' : items.map(scalarToText).join(' | ');
  }
  if (typeof value === 'object') {
    const pairs = Object.entries(value as Record<string, unknown>);
    if (pairs.length === 0) return 'Empty';
    return pairs.map(([key, val]) => `${key}: ${scalarToText(val)}`).join(' | ');
  }
  return scalarToText(value);
}

/** Leaf stringifier. Nested structures are JSON-printed rather than allowed to
 *  stringify to `[object Object]` — the API rejects array-of-objects attributes,
 *  so this only guards against an unexpected shape, but a visible `{"a":1}` is
 *  debuggable where `[object Object]` is not. */
function scalarToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  // Values originate as JSON, so nothing here can be a function or symbol —
  // the shapes `JSON.stringify` returns `undefined` for cannot arrive.
  return JSON.stringify(value);
}
